<template>
  <modal class="style-modal-wide" @close-modal="close">
    <main class="two-fa-enroll mt-1">
      <div class="container alignment">
        <div class="enroll-header">
          <div class="enroll-header__badge">
            <font-awesome-icon :icon="faShieldHalved" />
          </div>
          <h2 class="enroll-header__title my-0">{{ title }}</h2>
        </div>

        <!-- STEP 1: pick the factor -->
        <template v-if="step === 'pick'">
          <p class="instruction my-0 pb-3">
            Choose how you'd like to verify sensitive actions like withdrawing to
            a new crypto address.
          </p>
          <div class="method-selector">
            <div
              class="method-card"
              :class="{ selected: chosen === 'totp' }"
              @click="chosen = 'totp'"
            >
              <div class="card-icon">
                <font-awesome-icon :icon="faMobileScreen" />
              </div>
              <h4>Authenticator app</h4>
              <p>Google Authenticator, Authy, etc. (recommended)</p>
            </div>
            <div
              class="method-card"
              :class="{ selected: chosen === 'email' }"
              @click="chosen = 'email'"
            >
              <div class="card-icon">
                <font-awesome-icon :icon="faEnvelope" />
              </div>
              <h4>Email code</h4>
              <p>
                We email a 6-digit code to
                {{ userStore.user.userEmail || 'your email' }}
              </p>
            </div>
          </div>
          <p v-if="error" class="error my-0 pb-2">{{ error }}</p>
          <div class="d-flex justify-content-center mt-2 p-0">
            <button
              class="btn btn-primary mb-2 createButton bold"
              :disabled="!chosen || busy"
              @click="onPickContinue"
            >
              <b>{{ busy ? 'Starting…' : 'Continue' }}</b>
            </button>
          </div>
        </template>

        <!-- STEP 1b: capture an email first (email factor chosen, none on file) -->
        <template v-else-if="step === 'email'">
          <p class="instruction my-0 pb-3">
            Enter the email you would like to use, and confirm your account
            password:
          </p>
          <div class="code-row py-2">
            <input
              v-model="emailInput"
              class="codeInput email"
              type="email"
              autocomplete="email"
              placeholder="you@example.com"
            />
          </div>
          <div class="code-row py-2">
            <input
              v-model="emailPassword"
              class="codeInput email"
              type="password"
              autocomplete="current-password"
              placeholder="Account password"
              @keyup.enter="saveEmailAndContinue"
            />
          </div>
          <p v-if="error" class="error my-0 pb-2">{{ error }}</p>
          <div class="d-flex justify-content-center mt-2 p-0">
            <button
              class="btn btn-primary mb-2 createButton bold"
              :disabled="!emailValid || !emailPassword || busy"
              @click="saveEmailAndContinue"
            >
              <b>{{ busy ? 'Saving…' : 'Save & Continue' }}</b>
            </button>
          </div>
        </template>

        <!-- STEP 2 (door 1): prove the NEW factor -->
        <template v-else-if="step === 'door1'">
          <!-- TOTP: guided, two-step setup with the QR + manual key + code -->
          <template v-if="chosen === 'totp'">
            <div class="totp-setup">
              <div class="totp-step">
                <span class="totp-step__num">1</span>
                <div class="totp-step__body">
                  <p class="totp-step__title">Scan with your authenticator app</p>
                  <p class="totp-step__desc">
                    Open Google Authenticator (or Authy, 1Password, etc.), tap
                    <strong>+</strong> → <strong>Scan a QR code</strong>, and point
                    your phone's camera at the code below.
                  </p>
                </div>
              </div>

              <div v-if="otpauthUri" class="qr-wrap">
                <div class="qr-frame">
                  <qrcode-vue :value="otpauthUri" :size="190" level="H" render-as="svg" />
                </div>
                <p class="secret-hint">
                  Can't scan? Enter this key manually
                </p>
                <code class="secret">{{ manualSecret }}</code>
              </div>

              <div class="totp-step">
                <span class="totp-step__num">2</span>
                <div class="totp-step__body">
                  <p class="totp-step__title">Enter the 6-digit code</p>
                  <p class="totp-step__desc">
                    Type the code your authenticator app generates.
                  </p>
                </div>
              </div>

              <div class="code-row">
                <input
                  v-model="door1Code"
                  class="codeInput"
                  type="text"
                  inputmode="numeric"
                  autocomplete="one-time-code"
                  maxlength="6"
                  placeholder="000000"
                  @keyup.enter="confirmDoor1"
                />
              </div>
            </div>
          </template>

          <!-- Email: just enter the emailed code -->
          <template v-else>
            <p class="instruction my-0 pb-2">
              Enter the 6-digit code we emailed to {{ userStore.user.userEmail }}.
            </p>
            <div class="code-row py-2">
              <input
                v-model="door1Code"
                class="codeInput"
                type="text"
                inputmode="numeric"
                autocomplete="one-time-code"
                maxlength="6"
                placeholder="000000"
                @keyup.enter="confirmDoor1"
              />
            </div>
            <p class="resend my-0 pb-1">
              Didn't get it?
              <span
                class="resend-link"
                :class="{ disabled: resendDisabled }"
                @click="resendDoor1"
              >
                {{ resendLabel }}
              </span>
            </p>
          </template>

          <p v-if="error" class="error my-0 pb-2">{{ error }}</p>
          <div class="d-flex justify-content-center mt-2 p-0">
            <button
              class="btn btn-primary mb-2 createButton bold"
              :disabled="!door1Valid || busy"
              @click="confirmDoor1"
            >
              <b>{{ busy ? 'Verifying…' : 'Confirm' }}</b>
            </button>
          </div>
          <!-- Escape hatch back to the picker — critical for the email factor:
               if the address on file is wrong, the code never arrives, and the
               authenticator path needs no email at all. -->
          <p class="resend my-0 pb-1">
            <span
              class="resend-link"
              :class="{ disabled: busy }"
              @click="backToPicker"
            >
              ← Choose a different method
            </span>
          </p>
        </template>

        <!-- STEP 3 (door 2): confirm the transition with the CURRENT factor
             (or password, when enabling your first factor) -->
        <template v-else-if="step === 'door2'">
          <p class="instruction my-0 pb-2">
            <template v-if="gateMethod === 'password'">
              Enter your account password to finish enabling 2FA.
            </template>
            <template v-else-if="gateMethod === 'totp'">
              Enter the code from your current authenticator app to confirm this change.
            </template>
            <template v-else>
              Enter the code we emailed to your current address to confirm this change.
            </template>
          </p>

          <div class="code-row py-2">
            <input
              v-model="door2Proof"
              class="codeInput"
              :class="{ wide: gateMethod === 'password' }"
              :type="gateMethod === 'password' ? 'password' : 'text'"
              :inputmode="gateMethod === 'password' ? 'text' : 'numeric'"
              :placeholder="gateMethod === 'password' ? 'Password' : '000000'"
              @keyup.enter="confirmDoor2"
            />
          </div>
          <p v-if="gateMethod === 'email'" class="resend my-0 pb-1">
            Didn't get it?
            <span
              class="resend-link"
              :class="{ disabled: resendDisabled }"
              @click="resendDoor1"
            >
              {{ resendLabel }}
            </span>
          </p>
          <p v-if="error" class="error my-0 pb-2">{{ error }}</p>
          <div class="d-flex justify-content-center mt-2 p-0">
            <button
              class="btn btn-primary mb-2 createButton bold"
              :disabled="!door2Proof || busy"
              @click="confirmDoor2"
            >
              <b>{{ busy ? 'Activating…' : 'Activate 2FA' }}</b>
            </button>
          </div>
        </template>

        <!-- DONE -->
        <template v-else-if="step === 'done'">
          <p class="instruction success my-0 py-3">
            ✅ Two-factor authentication is now active using
            {{ chosen === 'totp' ? 'your authenticator app' : 'email codes' }}.
          </p>
          <div class="d-flex justify-content-center mt-2 p-0">
            <button class="btn btn-primary mb-2 createButton bold" @click="finish">
              <b>Done</b>
            </button>
          </div>
        </template>
      </div>
    </main>
  </modal>
</template>

<script setup>
import { ref, computed, onMounted, onUnmounted } from 'vue';
import QrcodeVue from 'qrcode.vue';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import { faMobileScreen } from '@fortawesome/free-solid-svg-icons/faMobileScreen';
import { faEnvelope } from '@fortawesome/free-solid-svg-icons/faEnvelope';
import { faShieldHalved } from '@fortawesome/free-solid-svg-icons/faShieldHalved';
import Modal from '@/components/global/WideModal.vue';
import { useUserStore } from '@/stores/user';
import { useTwoFactor } from '@/composables/useTwoFactor';
import { useAPI } from '@/composables/useAPI';
import { emitter } from '@/core/emitter';
import { PostHogUtils } from '@/core/PostHogUtils';

// Where enrollment was opened from — 'withdrawal' | 'banner' | 'account'.
const props = defineProps({
  source: { type: String, default: 'account' },
});

// enrolled — 2FA was activated (door 2 passed). Parent should refresh user state.
// closeModal — dismissed without finishing.
const emit = defineEmits(['enrolled', 'closeModal']);

const userStore = useUserStore();
const { startEnrollment, verify, issueCode, getActiveMethod } = useTwoFactor();

// The user's current active factor, if any — used only to title the modal
// ("Change" vs "Enable"). Fetched on open since login/getMe don't carry it.
const currentMethod = ref('');

const step = ref('pick'); // pick -> [email] -> door1 -> door2 -> done
const chosen = ref('totp'); // 'totp' | 'email'
const busy = ref(false);
const error = ref('');

// Email-on-file capture: the email factor can't issue a code without an address.
// If the user picks email and has none, we collect + save one first (recovery).
const emailInput = ref('');
const emailPassword = ref('');
const emailValid = computed(() => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(emailInput.value.trim()));

// Authoritatively check whether the account already has an email. We CAN'T trust
// userStore.user.userEmail here — it's only populated after visiting Account
// settings (login/getMe don't return it), so opening this modal from the banner
// would otherwise wrongly think there's no email. getAdvancedSettings.email is
// the source of truth.
async function accountHasEmail() {
  if (userStore.user?.userEmail) return true;
  try {
    const { request } = useAPI('/user/getAdvancedSettings');
    const res = await request();
    if (res?.email) {
      userStore.setUserEmail(res.email); // cache it so later steps show it
      return true;
    }
  } catch {
    // If we can't tell, fall through to collecting one — a needless email step
    // is far better than a confusing "Network Error" from issuing with no email.
  }
  return false;
}

// Door 1 (prove the new factor)
const door1RequestId = ref(null);
const otpauthUri = ref('');
const door1Code = ref('');

// Door 2 (confirm transition with current factor / password)
const door2RequestId = ref(null);
const gateMethod = ref('password');
const door2Proof = ref('');

const title = computed(() => {
  if (step.value === 'done') return '2FA Enabled';
  return currentMethod.value
    ? 'Change Two-Factor Method'
    : 'Enable Two-Factor Authentication';
});

// The shared secret pulled out of the otpauth:// URI, for manual entry when a
// user can't scan the QR.
const manualSecret = computed(() => {
  try {
    return new URL(otpauthUri.value).searchParams.get('secret') || '';
  } catch {
    return '';
  }
});

const door1Valid = computed(() => /^\d{6}$/.test(door1Code.value.trim()));

// Resend throttle: a per-click cooldown plus a hard cap on resends, so the email
// code can't be spammed. Cooldown grows with each resend (30s, 60s, 90s…).
const RESEND_COOLDOWN_BASE = 30; // seconds
const RESEND_MAX = 4;
const resendCount = ref(0);
const resendCooldown = ref(0); // seconds remaining
let resendTimer = null;

const resendDisabled = computed(
  () => busy.value || resendCooldown.value > 0 || resendCount.value >= RESEND_MAX,
);

const resendLabel = computed(() => {
  if (busy.value) return 'Sending…';
  if (resendCount.value >= RESEND_MAX) return 'Resend limit reached';
  if (resendCooldown.value > 0) return `Resend in ${resendCooldown.value}s`;
  return 'Resend code';
});

function startResendCooldown() {
  resendCount.value += 1;
  resendCooldown.value = RESEND_COOLDOWN_BASE * resendCount.value;
  if (resendTimer) clearInterval(resendTimer);
  resendTimer = setInterval(() => {
    resendCooldown.value -= 1;
    if (resendCooldown.value <= 0) {
      clearInterval(resendTimer);
      resendTimer = null;
    }
  }, 1000);
}

onMounted(async () => {
  PostHogUtils.capture('twofa_enroll_started', { source: props.source });
  try {
    currentMethod.value = (await getActiveMethod()) || '';
  } catch {
    currentMethod.value = ''; // can't tell — default to the "Enable" title
  }
});

onUnmounted(() => {
  if (resendTimer) clearInterval(resendTimer);
});

function close() {
  emit('closeModal');
}

// From the method picker: if email was chosen and the account has NO email on
// file, detour to the email-capture step first; otherwise go straight to door 1.
async function onPickContinue() {
  if (!chosen.value) return;
  error.value = '';
  PostHogUtils.capture('twofa_enroll_method_chosen', { method: chosen.value });
  if (chosen.value === 'email') {
    busy.value = true;
    try {
      const hasEmail = await accountHasEmail();
      if (!hasEmail) {
        emailInput.value = '';
        step.value = 'email';
        return;
      }
    } finally {
      busy.value = false;
    }
  }
  beginEnrollment();
}

// Save the entered email to the account, then continue into enrollment so the
// email factor has an address to send the code to.
async function saveEmailAndContinue() {
  if (!emailValid.value || !emailPassword.value) return;
  busy.value = true;
  error.value = '';
  try {
    const { request } = useAPI('/user/updateEmail');
    const lowerCaseEmail = emailInput.value.trim().toLowerCase();
    const response = await request({
      email: lowerCaseEmail,
      currentPassword: emailPassword.value,
    });
    if (!response?.success) {
      throw new Error(response?.message || 'Could not save your email.');
    }
    emailPassword.value = '';
    userStore.setUserEmail(lowerCaseEmail);
    await beginEnrollment();
  } catch (e) {
    error.value = e.message || 'Could not save your email.';
  } finally {
    busy.value = false;
  }
}

// Door 1: stage a pending credential and issue its challenge.
async function beginEnrollment() {
  if (!chosen.value) return;
  busy.value = true;
  error.value = '';
  try {
    const res = await startEnrollment({ method: chosen.value });
    door1RequestId.value = res.requestId;
    otpauthUri.value = (res.issued && res.issued.otpauthUri) || '';
    step.value = 'door1';
  } catch (e) {
    error.value = e.message || 'Could not start enrollment.';
  } finally {
    busy.value = false;
  }
}

// Bail out of door 1 back to the method picker (e.g. the emailed code never
// arrived because the address on file is wrong — the authenticator path needs
// no email). The abandoned request just lapses server-side (latest-pending-wins).
function backToPicker() {
  if (busy.value) return;
  PostHogUtils.capture('twofa_enroll_back_to_picker', { from: chosen.value });
  error.value = '';
  door1Code.value = '';
  door1RequestId.value = null;
  otpauthUri.value = '';
  step.value = 'pick';
}

async function resendDoor1() {
  if (resendDisabled.value) return;
  busy.value = true;
  error.value = '';
  try {
    await issueCode();
    startResendCooldown();
  } catch (e) {
    error.value = e.message || 'Could not resend the code.';
  } finally {
    busy.value = false;
  }
}

// Confirm door 1 → backend marks the new credential verified and opens door 2,
// returning the transition request id and which factor gates it.
async function confirmDoor1() {
  if (!door1Valid.value) return;
  busy.value = true;
  error.value = '';
  try {
    const res = await verify({
      requestId: door1RequestId.value,
      proof: door1Code.value.trim(),
    });
    const result = res && res.result;
    if (!result || !result.transitionRequestId) {
      throw new Error('Unexpected response from server.');
    }
    door2RequestId.value = result.transitionRequestId;
    gateMethod.value = result.gateMethod || 'password';
    step.value = 'door2';
    // Door 2 was just created but its challenge isn't auto-sent. For an email
    // gate, issue the code now so it actually reaches the user's inbox (without
    // this, the door-2 confirmation email never sends). TOTP/password need no
    // issuance. Reset the resend allowance for this fresh door-2 challenge.
    if (gateMethod.value === 'email') {
      resendCount.value = 0;
      resendCooldown.value = 0;
      try {
        await issueCode();
        startResendCooldown();
      } catch {
        // leave them on door 2; they can tap Resend
      }
    }
  } catch (e) {
    PostHogUtils.capture('twofa_enroll_failed', { step: 'door1' });
    error.value = e.message || 'That code was not accepted.';
    door1Code.value = '';
  } finally {
    busy.value = false;
  }
}

// Confirm door 2 with the CURRENT factor (or password for the very first one) →
// promotes the new credential to active and retires the old.
async function confirmDoor2() {
  if (!door2Proof.value) return;
  busy.value = true;
  error.value = '';
  try {
    await verify({
      requestId: door2RequestId.value,
      proof: door2Proof.value,
    });
    step.value = 'done';
  } catch (e) {
    PostHogUtils.capture('twofa_enroll_failed', { step: 'door2' });
    error.value = e.message || 'Could not confirm. Please try again.';
    door2Proof.value = '';
  } finally {
    busy.value = false;
  }
}

async function finish() {
  // Pull fresh user state so anything reading the user object reflects the
  // change, and let listeners (e.g. account settings) refresh their 2FA status.
  try {
    await userStore.getMe();
  } catch {
    // best-effort; the activation already succeeded server-side
  }
  PostHogUtils.capture('twofa_enroll_completed', { method: chosen.value });
  emitter.emit('twoFactorMethodChanged');
  emit('enrolled');
}
</script>

<style scoped lang="scss">
@import '@/scss/variables.scss';
@import '@/scss/theme/variables.scss';

.alignment {
  text-align: -moz-center;
  text-align: -webkit-center;
}

.two-fa-enroll {
  color: $black;

  // Header: icon badge + title with a divider, so the title clearly leads.
  .enroll-header {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 0.6rem;
    padding-bottom: 1rem;
    margin-bottom: 1.25rem;
    border-bottom: 1px solid $grey-601;

    &__badge {
      display: flex;
      align-items: center;
      justify-content: center;
      width: 46px;
      height: 46px;
      border-radius: 13px;
      background: linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%);
      color: #fff;
      font-size: 1.25rem;
      box-shadow: 0 6px 16px -4px rgba(99, 102, 241, 0.6);
    }

    &__title {
      font-size: 1.35rem;
      font-weight: 700;
      letter-spacing: -0.2px;
      color: $grey-2700;
      line-height: 1.2;
    }
  }

  .instruction {
    font-size: 0.95rem;
    color: $grey-2400;

    &.success {
      color: $black;
      font-size: 1.05rem;
    }
  }

  // Side-by-side selectable cards, mirroring the signup account-type selector.
  .method-selector {
    display: flex;
    gap: 1rem;
    margin-bottom: 1.5rem;
  }

  .method-card {
    flex: 1;
    text-align: center;
    padding: 1.5rem 1rem;
    border: 2px solid $grey-601;
    border-radius: 12px;
    background: $white_01;
    cursor: pointer;
    transition: all 0.2s ease;

    &:hover {
      border-color: $blue-100;
      transform: translateY(-2px);
      box-shadow: 0 4px 12px rgba(105, 111, 177, 0.12);
    }

    &.selected {
      border-color: $blue-100;
      background: rgba(105, 111, 177, 0.06);
      box-shadow: 0 4px 12px rgba(105, 111, 177, 0.12);
    }

    .card-icon {
      font-size: 1.75rem;
      margin-bottom: 0.5rem;
      color: $grey-2400;
      transition: color 0.2s;
    }

    &.selected .card-icon,
    &:hover .card-icon {
      color: $blue-100;
    }

    h4 {
      margin: 0;
      font-size: 0.95rem;
      font-weight: 600;
      color: $grey-2700;
    }

    p {
      margin: 0.25rem 0 0;
      font-size: 12px;
      color: $grey-2400;
    }
  }

  // Guided TOTP setup — numbered steps + framed QR for a clean, pro feel.
  .totp-setup {
    display: flex;
    flex-direction: column;
    gap: 1.1rem;
    text-align: left;
    margin: 0.25rem auto 0;
    max-width: 360px;
  }

  // Center the code/email input within its column (fixes the off-center input).
  .code-row {
    display: flex;
    justify-content: center;
  }

  .totp-step {
    display: flex;
    align-items: flex-start;
    gap: 0.7rem;

    &__num {
      flex-shrink: 0;
      display: flex;
      align-items: center;
      justify-content: center;
      width: 26px;
      height: 26px;
      border-radius: 50%;
      background: $blue-500;
      color: #fff;
      font-size: 0.85rem;
      font-weight: 700;
    }

    &__body {
      flex: 1;
      min-width: 0;
    }

    &__title {
      margin: 0;
      font-size: 0.92rem;
      font-weight: 600;
      color: $grey-2700;
    }

    &__desc {
      margin: 0.15rem 0 0;
      font-size: 0.8rem;
      line-height: 1.4;
      color: $grey-2400;

      strong {
        color: $grey-2700;
        font-weight: 600;
      }
    }
  }

  .qr-wrap {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 0.4rem;

    .qr-frame {
      padding: 12px;
      background: #fff;
      border: 1px solid $grey-601;
      border-radius: 14px;
      box-shadow: 0 4px 14px rgba(0, 0, 0, 0.08);
      line-height: 0;
    }

    .secret-hint {
      margin: 0.5rem 0 0;
      font-size: 0.76rem;
      color: $grey-2400;
    }

    .secret {
      display: inline-block;
      font-family: ui-monospace, monospace;
      font-size: 0.8rem;
      letter-spacing: 0.5px;
      background: rgba(0, 0, 0, 0.05);
      color: $grey-2700;
      padding: 0.3rem 0.6rem;
      border-radius: 6px;
      word-break: break-all;
    }
  }

  .codeInput {
    width: 13rem;
    max-width: 100%;
    text-align: center;
    letter-spacing: 0.55rem;
    font-size: 1.5rem;
    font-weight: 600;
    // Fixed dark color: the input background is always light/white, so the
    // typed code must NOT use a theme variable that flips to light in dark mode
    // (that made the code invisible on the white field).
    color: #1f2937;
    border: 1.5px solid $grey-601;
    border-radius: 12px;
    padding: 0.7rem 0.5rem;
    background: #fff;
    transition: border-color 0.18s ease, box-shadow 0.18s ease, background 0.18s ease;

    &::placeholder {
      color: #9ca3af;
      opacity: 1;
    }

    &:focus {
      outline: none;
      border-color: #6366f1;
      background: #fff;
      box-shadow: 0 0 0 4px rgba(99, 102, 241, 0.16);
    }

    &.wide {
      width: 16rem;
      letter-spacing: normal;
      font-size: 1rem;
      font-weight: 500;
      text-align: left;
    }

    // Email-entry variant (recovery step): normal text, no code spacing.
    &.email {
      width: 22rem;
      letter-spacing: normal;
      font-size: 1rem;
      font-weight: 500;
      text-align: left;
    }
  }

  .resend {
    font-size: 0.85rem;
    color: $grey-2400;

    .resend-link {
      color: $blue-500;
      cursor: pointer;
      text-decoration: underline;

      &.disabled {
        opacity: 0.6;
        cursor: default;
        text-decoration: none;
      }
    }
  }

  .error {
    color: $red-500;
    font-size: 0.85rem;
  }

  // Premium primary action: gradient pill with a soft lift on hover.
  .createButton.btn-primary {
    min-width: 12rem;
    padding: 0.7rem 1.5rem;
    border: none;
    border-radius: 999px;
    background: linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%);
    color: #fff;
    font-size: 0.95rem;
    letter-spacing: 0.2px;
    box-shadow: 0 6px 16px -4px rgba(99, 102, 241, 0.6);
    transition: transform 0.15s ease, box-shadow 0.15s ease, filter 0.15s ease;

    &:hover:not(:disabled) {
      transform: translateY(-1px);
      box-shadow: 0 10px 22px -6px rgba(99, 102, 241, 0.7);
      filter: brightness(1.04);
    }

    &:active:not(:disabled) {
      transform: translateY(0);
    }

    &:disabled {
      opacity: 0.5;
      box-shadow: none;
      cursor: not-allowed;
    }
  }
}
</style>
